import Data from 'data'
import Locale from 'data/locale'

import { Color, Quality } from 'common'
import { EmbedItemLink } from 'components/cclink'
import {
  itemGetColor,
  itemGetName,
  itemGetTypes,
  itemMaxSockets
} from 'logic/item'
import {
  itemCreateNormal,
  itemCreateRuneword,
  itemCreateUnique,
  itemFix,
  itemUpdate
} from 'logic/itemReducers'
import {
  craftingRecipes,
  isSubType,
  itemTypeNames,
  presetItems
} from 'logic/itemTypes'
import { D2ItemTooltip, D2Tooltip, ItemTooltip } from 'tooltips'
import { underlinerFunc } from 'ui/select'
import { combineFilters } from 'utils'

const _L = Locale('ui-equipment')

export const renderBase = ({ id, name, color, description }, search, isCC) => (
  <D2ItemTooltip base={id}>
    <span className={`d2-color-${color}`}>
      {search ? underlinerFunc(name, search) : name}
      {isCC && <EmbedItemLink id={id} text={name} />}
      {!!description && (
        <span className="d2p-item-description">{description}</span>
      )}
    </span>
  </D2ItemTooltip>
)

function findNode(node, type) {
  if (!node.filter(type)) return null
  let current = node
  while (current.children) {
    const next = current.children.find(
      (item) => !item.hidden && item.filter?.(type)
    )
    if (!next) break
    current = next
  }
  return current
}

function typesToNodes(root, filter) {
  const types = {}
  for (const id in Data.items) {
    const base = Data.items[id]
    if (!types.hasOwnProperty(base.type)) {
      if (filter && !filter(base.type)) {
        types[base.type] = null
      } else {
        types[base.type] = findNode(root, base.type)
      }
    }
  }
  return types
}

function itemOption(id) {
  const item = Data.items[id]
  return {
    id,
    name: Locale.strings[item.namestr] || item.name,
    color: Data.itemTypes[item.type]?.magic ? Color.BLUE : Color.WHITE
  }
}

function listBases(current, filter, idFilter) {
  const types = typesToNodes(current, filter)
  const nodeMap = new Map()
  for (const id in Data.items) {
    if (idFilter && !idFilter(id)) continue
    if (presetItems[id]) continue
    const base = Data.items[id]
    const node = types[base.type]
    if (!node) continue
    if (!nodeMap.has(node)) nodeMap.set(node, [])
    if (base.spawnable) nodeMap.get(node).push(id)
  }

  const optionList = (bases) =>
    bases
      .sort((a, b) => Data.items[b].level - Data.items[a].level)
      .map(itemOption)

  const options = []
  const nodes = [...nodeMap.keys()].sort((a, b) => a.name.localeCompare(b.name))
  for (const node of nodes) {
    const bases = nodeMap.get(node)
    if (bases.length <= 2) {
      options.push({
        title: _L(node.name),
        options: optionList(bases)
      })
    } else {
      const t1 = [],
        t2 = [],
        t3 = []
      for (const id of bases) {
        const item = Data.items[id]
        if (item.code === item.normcode || !item.normcode) t1.push(id)
        if (item.code === item.ubercode) t2.push(id)
        if (item.code === item.ultracode) t3.push(id)
      }
      if (t3.length) {
        options.push({
          title: _L`Elite ${node.name}`,
          options: optionList(t3)
        })
      }
      if (t2.length) {
        options.push({
          title: _L`Exceptional ${node.name}`,
          options: optionList(t2)
        })
      }
      if (t1.length) {
        options.push({
          title: _L`Normal ${node.name}`,
          options: optionList(t1)
        })
      }
    }
  }
  return options
}

export const itemCategories = {}

itemCategories.normal = {
  //title: () => <span className={`d2-color-${Color.WHITE}`}>{_L`Normal Items`}</span>,
  title: () => (
    <span className={`d2-color-${Color.GRAY}`}>
      <span className={`d2-color-${Color.YELLOW}`}>Rare</span> /{' '}
      <span className={`d2-color-${Color.BLUE}`}>Magic</span>
    </span>
  ),
  list: (current, filter, searchFilter) =>
    listBases(
      current,
      combineFilters(filter, searchFilter?.type),
      combineFilters(searchFilter?.filter, searchFilter?.base)
    ),
  create: (id, quality) => itemCreateNormal(id, quality),
  link: (opt) => `item.${opt?.id}`,
  render: renderBase,
  bases: (id) => {
    const item = Data.items[id]
    const type = Data.itemTypes[item?.type]
    if (!type) return
    return [
      type.rare && { id: Quality.RARE, name: _L`Rare`, color: Color.YELLOW },
      !type.normal && { id: Quality.MAGIC, name: _L`Magic`, color: Color.BLUE },
      !type.magic && {
        id: Quality.SUPERIOR,
        name: _L`Superior`,
        color: Color.WHITE
      },
      !type.magic && {
        id: Quality.NORMAL,
        name: _L`Normal`,
        color: Color.WHITE
      },
      !type.magic && {
        id: Quality.LOW,
        name: _L`Low Quality`,
        color: Color.WHITE
      }
    ].filter(Boolean)
  }
}

export function itemCreateCrafted(id, base, template) {
  const mod = Data.crafted[id]
  if (!mod) return null

  const values = []
  for (let i = 1; i <= 4; ++i) {
    if (mod[`mod${i}code`]) {
      const min = mod[`mod${i}min`]
      const max = mod[`mod${i}max`]
      if (max && min < max) {
        values[i - 1] = template ? { min, max } : max
      } else {
        values[i - 1] = max
      }
    }
  }

  const item = {
    base,
    quality: Quality.CRAFTED,
    crafted: {
      [id]: values
    },
    mods: {},
    template
  }
  return template ? itemUpdate(item) : itemFix(item)
}

function CraftedItemTooltip({ id, base }) {
  const item = itemCreateCrafted(id, base, true)
  if (!item) return null
  return <ItemTooltip item={item} />
}

itemCategories.crafted = {
  title: () => (
    <span className={`d2-color-${Color.ORANGE}`}>{_L`Crafted Items`}</span>
  ),
  list(current, filter, searchFilter) {
    const recipes = {}

    const types = {}
    for (const [id, { type }] of Object.entries(Data.items)) {
      if (searchFilter?.base && !searchFilter.base(id)) continue
      if (!types.hasOwnProperty(type)) {
        if (current.filter(type) && (!filter || filter(type))) {
          types[type] = id
        } else {
          types[type] = null
        }
      }
    }

    for (const [id, recipe] of Object.entries(Data.crafted)) {
      const item = { id, color: Color.ORANGE }
      const input = recipe.input1.split(',')
      if (input[2] === 'eth') continue
      if (input[2] === 'noe' && recipe.mod5code === 'ethereal') continue // bows bug
      if (input[2] === 'upg') {
        const base = Data.items[input[0]]
        if (!types[base?.type]) continue
        if (searchFilter?.type && !searchFilter.type(id, base.type)) continue
        item.base = [base.normcode, base.ubercode, base.ultracode].find(
          (base) => !searchFilter?.base || searchFilter.base(base)
        )
        if (!item.base) continue
        item.name = Locale.strings[base.namestr] || base.name
      } else {
        const type = Object.keys(types).find(
          (type) =>
            types[type] &&
            isSubType(type, input[0]) &&
            (!searchFilter?.type || searchFilter.type(id, type))
        )
        if (!type) continue
        item.base = types[type]
        item.name = _L(itemTypeNames[input[0]])
      }
      recipes[recipe.description] = recipes[recipe.description] || []
      recipes[recipe.description].push(item)
    }

    return Object.entries(recipes).map(([id, options]) => ({
      title: _L(craftingRecipes[id]),
      options
    }))
  },
  bases(id, current, filter, searchFilter) {
    const mod = Data.crafted[id]
    if (!mod) return null
    const input = mod.input1.split(',')
    if (input[2] === 'upg') {
      const item = Data.items[input[0]]
      if (!item) return null
      return [item.normcode, item.ubercode, item.ultracode]
        .filter((base) => !searchFilter?.base || searchFilter.base(base))
        .map(itemOption)
    } else {
      filter = combineFilters(
        filter,
        searchFilter?.type && ((type) => searchFilter.type(id, type))
      )
      return listBases(
        current,
        (type) => isSubType(type, input[0]) && filter(type),
        searchFilter?.base
      )
    }
  },
  linkBases: true,
  create: (id, base) => itemCreateCrafted(id, base),
  link: (optId, optBase) => `item.${optId?.id}#${optBase?.id}`,
  render: ({ id, name, base }, search, isCC) => (
    <D2Tooltip tooltip={<CraftedItemTooltip base={base} id={id} />}>
      <span className={`d2-color-${Color.ORANGE}`}>
        {search ? underlinerFunc(name, search) : name}
        {isCC && <EmbedItemLink id={`${id}#${base}`} text={name} />}
      </span>
    </D2Tooltip>
  )
}

itemCategories.unique = {
  title: () => (
    <span className={`d2-color-${Color.GOLD}`}>{_L`Unique Items`}</span>
  ),
  list(current, filter, searchFilter) {
    const types = typesToNodes(current, filter)
    const nodeMap = new Map()
    for (const id in Data.uniqueItems) {
      const item = Data.uniqueItems[id]
      if (!item.enabled) continue
      if (searchFilter?.filter && !searchFilter.filter(id)) continue
      if (searchFilter?.base && !searchFilter?.base(item.code)) continue
      const base = Data.items[item.code]
      const node = base && types[base.type]
      if (!node) continue
      if (!nodeMap.has(node)) nodeMap.set(node, [])
      nodeMap.get(node).push(id)
    }

    const options = []
    const nodes = [...nodeMap.keys()].sort((a, b) =>
      a.name.localeCompare(b.name)
    )
    for (const node of nodes) {
      options.push({
        title: _L(node.name),
        options: nodeMap
          .get(node)
          .map((id) => {
            const item = Data.uniqueItems[id]
            const base = Data.items[item.code]
            return {
              id,
              name: Locale.strings[item.index] || item.index,
              baseName: Locale.strings[base?.namestr],
              color: Color.GOLD,
              description: item.suffix
            }
          })
          .sort((a, b) => a.name.localeCompare(b.name))
      })
    }
    return options
  },
  create: (id, base, profile) => itemCreateUnique(id, profile),
  link: (opt) => `item.${opt?.id}`,
  render: renderBase
}

itemCategories.set = {
  title: () => (
    <span className={`d2-color-${Color.GREEN}`}>{_L`Set Items`}</span>
  ),
  list(current, filter, searchFilter) {
    const types = typesToNodes(current, filter)
    const sets = {}
    for (const id in Data.setItems) {
      const item = Data.setItems[id]
      const base = Data.items[item.item]
      const node = base && types[base.type]
      if (!node) continue
      if (searchFilter?.filter && !searchFilter.filter(id)) continue
      if (searchFilter?.base && !searchFilter?.base(item.item)) continue
      const set = Data.sets[item.set]
      if (!set) continue
      const name = Locale.strings[set.name] || set.name
      if (!sets[name]) sets[name] = []
      sets[name].push(id)
    }

    const options = []
    for (const [name, items] of Object.entries(sets).sort()) {
      options.push({
        title: name,
        options: items
          .map((id) => {
            const item = Data.setItems[id]
            const base = Data.items[item.item]
            return {
              id,
              name: Locale.strings[item.index] || item.index,
              baseName: Locale.strings[base?.namestr],
              color: Color.GREEN
            }
          })
          .sort((a, b) => a.name.localeCompare(b.name))
      })
    }
    return options
  },
  create: (id, base, profile) => itemCreateUnique(id, profile),
  link: (opt) => `item.${opt?.id}`,
  render: renderBase
}

export function getRunewordSockets(runeword) {
  const unique = Data.runes[runeword]
  if (!unique) return 0
  return [
    unique.rune1,
    unique.rune2,
    unique.rune3,
    unique.rune4,
    unique.rune5,
    unique.rune6
  ].filter(Boolean).length
}
export function isValidRunewordBase(runeword, id, sockets) {
  const item = Data.runes[runeword]
  const base = Data.items[id]
  if (!base?.spawnable || !item) return false
  const typeSet = itemGetTypes(id)
  if (
    ![
      item.itype1,
      item.itype2,
      item.itype3,
      item.itype4,
      item.itype5,
      item.itype6
    ].some((t) => t && typeSet.has(t))
  )
    return false
  if ([item.etype1, item.etype2, item.etype3].some((t) => t && typeSet.has(t)))
    return false
  if (itemMaxSockets(base, Quality.NORMAL) < sockets) return false

  return true
}

const renderRuneword = ({ id, name, color }, search, isCC) => {
  const p = id.split('#')
  return (
    <D2ItemTooltip base={p[0]} type={p[1]}>
      <span className={`d2-color-${color}`}>
        {search ? underlinerFunc(name, search) : name}
        {isCC && <EmbedItemLink id={id} text={name} />}
      </span>
    </D2ItemTooltip>
  )
}

itemCategories.runeword = {
  title: () => (
    <span className={`d2-color-${Color.GOLD}`}>{_L`Runewords`}</span>
  ),
  list(current, filter, searchFilter) {
    const types = typesToNodes(current, filter)

    const typeSockets = {}
    for (const id in Data.items) {
      const base = Data.items[id]
      if (!base.spawnable) continue
      if (searchFilter?.base && !searchFilter.base(id)) continue
      if (types[base.type]) {
        typeSockets[base.type] = Math.max(
          typeSockets[base.type] || 0,
          itemMaxSockets(base, Quality.NORMAL)
        )
      }
    }

    const runewords = {}
    const rwtypes = ['weap', 'tors', 'shld', 'helm']
    const validTypes = Object.keys(types).filter((type) => types[type])
    for (const id in Data.runes) {
      const item = Data.runes[id]
      const sockets = [
        item.rune1,
        item.rune2,
        item.rune3,
        item.rune4,
        item.rune5,
        item.rune6
      ].filter(Boolean).length
      const vtypes = validTypes.filter((type) => {
        if (!typeSockets[type] || typeSockets[type] < sockets) return false
        if (
          ![
            item.itype1,
            item.itype2,
            item.itype3,
            item.itype4,
            item.itype5,
            item.itype6
          ].some((t) => t && isSubType(type, t))
        )
          return false
        if (
          [item.etype1, item.etype2, item.etype3].some(
            (t) => t && isSubType(type, t)
          )
        )
          return false
        return true
      })
      for (const rwt of rwtypes) {
        if (
          vtypes.some(
            (vt) => isSubType(vt, rwt) || (vt === 'pala' && rwt === 'shld')
          )
        ) {
          if (searchFilter?.filter && !searchFilter.filter(id, rwt)) continue
          runewords[rwt] = runewords[rwt] || []
          runewords[rwt].push(id)
        }
      }
    }

    return rwtypes
      .map(
        (rwt) =>
          runewords[rwt] && {
            title: _L(itemTypeNames[rwt]),
            options: runewords[rwt]
              .map((id) => {
                const item = Data.runes[id]
                return {
                  id: `${id}#${rwt}`,
                  name: Locale.strings[item.name] || item.name,
                  color: Color.GOLD
                }
              })
              .sort((a, b) => a.name.localeCompare(b.name))
          }
      )
      .filter(Boolean)
  },
  linkName(opt) {
    const p = opt?.id.split('#')
    const rw = Data.runes[p[0]]
    if (!rw) return
    return Locale.strings[rw.name]
  },
  bases(id, current, filter, searchFilter) {
    const p = id.split('#')
    const sockets = getRunewordSockets(p[0])
    return listBases(
      current,
      (t) => isSubType(t, p[1]) && (!filter || filter(t)),
      (base) =>
        (!searchFilter?.base || searchFilter.base(base)) &&
        isValidRunewordBase(p[0], base, sockets)
    )
  },
  link: (opt) => `item.${opt?.id}`,
  create: (id, base) => itemCreateRuneword(id.split('#')[0], base),
  render: renderRuneword
}

itemCategories.preset = {
  title: () => (
    <span className={`d2-color-${Color.WHITE}`}>{_L`Miscellaneous`}</span>
  ),
  list(current, filter, searchFilter) {
    const presetPotion = [],
      presetHeld = [],
      presetInventory = []
    for (const [id, item] of Object.entries(presetItems)) {
      const base = Data.items[item.base]
      if (!current.filter(base.type) && !current.preset) continue
      if (filter && !filter(base.type)) continue
      if (searchFilter?.type && !searchFilter?.type(base.type)) continue
      if (searchFilter?.filter && !searchFilter?.filter(item.base)) continue
      if (searchFilter?.base && !searchFilter?.base(item.base)) continue
      if (isSubType(base.type, 'sock')) continue
      const row = { id, name: itemGetName(item), color: itemGetColor(item) }
      if (isSubType(base.type, 'poti')) {
        presetPotion.push(row)
      } else if (isSubType(base.type, 'misl') || isSubType(base.type, 'weap')) {
        presetHeld.push(row)
      } else {
        presetInventory.push(row)
      }
    }
    function makeList(list, title) {
      if (!list.length) return null
      return {
        title,
        options: list.sort((a, b) => {
          if (a.color !== b.color) return a.color - b.color
          return a.name.localeCompare(b.name)
        })
      }
    }
    return [
      makeList(presetHeld, _L`Accessories`),
      makeList(presetPotion, _L`Potions`),
      makeList(presetInventory, _L`Inventory`)
    ].filter(Boolean)
  },
  link: (opt) => `item.${opt?.id}`,
  render: renderBase
}

itemCategories.socket = {
  title: () => (
    <span className={`d2-color-${Color.ORANGE}`}>{_L`Socketable`}</span>
  ),
  list(current, filter, searchFilter) {
    const presetRune = [],
      presetGem = []
    for (const [id, item] of Object.entries(presetItems)) {
      const base = Data.items[item.base]
      if (!current.filter(base.type) && !current.preset) continue
      if (filter && !filter(base.type)) continue
      if (searchFilter?.type && !searchFilter?.type(base.type)) continue
      if (searchFilter?.filter && !searchFilter?.filter(item.base)) continue
      if (!isSubType(base.type, 'sock')) continue
      const row = { id, name: itemGetName(item), color: itemGetColor(item) }
      if (isSubType(base.type, 'rune')) {
        presetRune.push(row)
      } else {
        presetGem.push(row)
      }
    }
    function makeList(list, title) {
      if (!list.length) return null
      return {
        title,
        options: list
      }
    }
    return [
      makeList(presetRune, _L`Runes`),
      makeList(presetGem, _L`Gems`)
    ].filter(Boolean)
  },
  link: (opt) => `item.${opt?.id}`,
  render: renderBase
}
